import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useForgotPassword } from '@/api/hooks/usePasskeys';
import { ApiError } from '@/api/client';
import { LogoFull } from '@/components/LogoFull';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { extractValidationErrors } from '@/utils/errorHandler';
import { trackAnonymous } from '@/components/PostHogTracker';

export function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [faxNumber, setFaxNumber] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [generalError, setGeneralError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const forgotPasswordMutation = useForgotPassword();
    const isSubmitting = forgotPasswordMutation.isPending;

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        setErrors({});
        setGeneralError(null);

        // Client-side validation
        const newErrors: Record<string, string> = {};

        if (!email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            await forgotPasswordMutation.mutateAsync({ email, faxNumber });
            trackAnonymous('forgot_password_submitted');
            setSuccess(true);
        } catch (err) {
            if (err instanceof ApiError) {
                const validationErrors = extractValidationErrors(err);
                if (Object.keys(validationErrors).length > 0) {
                    setErrors(validationErrors);
                } else {
                    setGeneralError(err.message || 'Failed to send reset email. Please try again.');
                }
            } else {
                setGeneralError('An unexpected error occurred');
            }
        }
    }

    if (success) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="h-10 w-auto mx-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Check your email
                        </h1>

                        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                            If an account with that email exists, we've sent you a link to reset your password.
                            The link will expire in 1 hour.
                        </div>

                        <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
                            Didn't receive the email? Check your spam folder or try again.
                        </p>

                        <div className="space-y-4">
                            <Button
                                type="button"
                                variant="secondary"
                                className="w-full"
                                size="lg"
                                onClick={() => setSuccess(false)}
                            >
                                Try again
                            </Button>

                            <Link to="/login" className="block">
                                <Button type="button" variant="secondary" className="w-full" size="lg">
                                    Back to login
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <Link to="/">
                        <LogoFull className="h-10 w-auto mx-auto" />
                    </Link>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                    <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                        Forgot your password?
                    </h1>
                    <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        Enter your email address and we'll send you a link to reset your password.
                    </p>

                    {generalError && (
                        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                            {generalError}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <input
                            type="text"
                            name="fax_number"
                            value={faxNumber}
                            onChange={(e) => setFaxNumber(e.target.value)}
                            autoComplete="new-password"
                            tabIndex={-1}
                            style={{ position: 'absolute', left: '-9999px' }}
                            aria-hidden="true"
                        />
                        <Input
                            type="email"
                            id="email"
                            label="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="you@example.com"
                            error={errors.email}
                        />

                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            isLoading={isSubmitting}
                            className="w-full"
                            size="lg"
                        >
                            {isSubmitting ? 'Sending...' : 'Send reset link'}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <Link
                            to="/login"
                            className="text-sm font-medium text-gray-600 underline hover:no-underline dark:text-gray-300"
                        >
                            Back to login
                        </Link>
                    </div>
                </div>

                <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    Securely manage your financial data
                </p>
            </div>
        </div>
    );
}
